import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** A titled card section; the building block of Idag and Plan. */
export function Panel({
  title,
  description,
  action,
  children,
  className,
  tone = 'default',
}: {
  title?: ReactNode;
  description?: ReactNode;
  /** Right side of the header, usually a small link */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  tone?: 'default' | 'warning';
}) {
  return (
    <section
      className={cn(
        'rounded-2xl border p-4 sm:p-5',
        tone === 'warning' ? 'border-warning/25 bg-warning-soft' : 'border-border bg-card',
        className,
      )}
    >
      {(title || action) && (
        <header className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title && <h2 className="text-[15px] font-semibold leading-6">{title}</h2>}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          {action && (
            // Header links are small text; stretch their hit area toward 44px.
            <div className="shrink-0 text-sm max-sm:[&_a]:relative max-sm:[&_a]:after:absolute max-sm:[&_a]:after:-inset-x-2 max-sm:[&_a]:after:-inset-y-3">
              {action}
            </div>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

/** Thin progress bar with an optional "where pace says you should be" tick */
export function Meter({
  value,
  marker,
  tone = 'primary',
  label,
  className,
}: {
  /** 0..1 */
  value: number;
  /** 0..1, drawn as a tick */
  marker?: number | undefined;
  tone?: 'primary' | 'positive' | 'warning' | 'negative';
  label: string;
  className?: string;
}) {
  const fill = {
    primary: 'bg-primary',
    positive: 'bg-positive',
    warning: 'bg-warning',
    negative: 'bg-negative',
  }[tone];
  return (
    <div
      role="img"
      aria-label={label}
      className={cn('relative h-2 rounded-full bg-muted', className)}
    >
      <div
        className={cn('h-full rounded-full transition-[width]', fill)}
        style={{ width: `${Math.min(Math.max(value, 0), 1) * 100}%` }}
      />
      {marker != null && (
        <div
          className="absolute -top-1 h-4 w-0.5 rounded-full bg-foreground/60"
          style={{ left: `calc(${Math.min(Math.max(marker, 0), 1) * 100}% - 1px)` }}
        />
      )}
    </div>
  );
}
